import {
  Panel,
  Group,
  Separator,
  type GroupImperativeHandle,
  type Layout,
  type PanelImperativeHandle,
  type PanelSize,
} from 'react-resizable-panels';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectAllChatDocuments,
  selectChatDocumentPanelSettings,
  selectChatDocumentsDownload,
  selectChatTitle,
  setChatDocumentPanelSettings,
  clearSessionDocuments,
  selectChatId,
  selectPendingUrlSyncChatId,
  selectIgnoreSharedLinkChatIdSync,
  setChatId,
  setPendingUrlSyncChatId,
  setIgnoreSharedLinkChatIdSync,
  clearChatButKeepId,
  selectChatMatterInfo,
  selectIsChatCreator,
  selectChatHistoryRowPanel,
  closeChatHistoryRowPanel,
} from '@/store/slices/chat/chat';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { ChatTemplate } from '@/ui/templates';
import { CitationsPanel } from './CitationsPanel';
import { ChatDocumentSidePanel } from './components/ChatDocumentSidePanel';
import { ChatHistoryRowSidePanel } from './components/ChatHistoryRowSidePanel';
import { ChatScrollableMessageArea } from './components/ChatScrollableMessageArea';
import { ChatStatusAnnouncer } from './components/ChatStatusAnnouncer';
import {
  BackIcon,
  CommentIcon,
  DocumentsIcon,
  LoadingSpinner,
  MinimizeIcon,
  QuoteOutlineIcon,
  SearchIcon,
  SettingsOutlineIcon,
} from '@repo/atomic-components';
import { useSourcesPanelDocuments } from '@/ui/pages/ChatPage/hooks';
import { useFrostedGlassClass } from '@/ui/theme/useFrostedGlassClass';
import { useFileDragAndDrop } from '@/ui/hooks/useFileDragAndDrop';
import { ChatInput, type ChatInputProps } from '@/ui/features/chat/components/ChatInput';
import { DragAndDropOverlay } from '@/ui/components/chat/DragAndDropOverlay';
import { ChatDisclaimer } from '@/ui/components/chat/ChatDisclaimer';
import { ToolkitCarousel } from './components/ToolkitCarousel';
import { CHAT_INPUT_TEXTAREA_ID } from '@/ui/features/chat/components/NextSteps';
import { AppRoutes, projectChatPath } from '@/constants';
import { ChatSearchBar, ChatSearchInputBar } from './components/ChatSearchBar';
import { HeaderIconButton } from './components/HeaderIconButton';
import { ChatHeaderMoreMenu, type ChatHeaderMenuItem } from './components/ChatHeaderMoreMenu';
import { EditableChatTitle } from './components/EditableChatTitle';
import { ChatDownloadFilesModal } from './components/ChatDownloadFilesModal';
import { ChatSettingsModal } from './components/ChatSettingsModal';
import { useChatContext } from '@/ui/features/chat/useChatContext';
import type { CaseMentionState } from '@/ui/features/chat/hooks/useCaseMention';
import { useChatSearch, type OpenDraftMatchCount } from './hooks/useChatSearch';
import { useMinimizedChatInput } from './hooks/useMinimizedChatInput';
import { MinimizedChatInputButton } from './components/MinimizedChatInputButton';
import {
  CHAT_COLUMN_ATTR,
  CHAT_INPUT_DOCK_ATTR,
  CHAT_INPUT_DOCK_HEIGHT_OFFSET_ATTR,
} from '@/ui/features/chat/hooks/useChatInputMaxHeight';
import { useAutoSendInitialChatPrompt, type ChatPageLocationState } from './hooks/useAutoSendInitialChatPrompt';
import { useEventCallback, useIsCompactLayout, useMediaQuery } from '@/hooks';
import {
  useGetCasesQuery,
  useGetChatDocumentsQuery,
  useGetChatQuery,
  useGetMatterAboutQuery,
  useLazyGetChatDocumentsQuery,
  useLazyGetChatQuery,
  useGetDraftVersionMutation,
  useGetDraftDownloadMutation,
  useGetDraftCitationIdsQuery,
  useLazyGetDraftCitationIdsQuery,
  useSaveDraftMutation,
} from '@/api';
import type { GetChatResponse } from '@/api/chat/getChat';
import { isUnpromptedChat } from './utils/isUnpromptedChat';
import type { Citation, DraftInfo, MessageAttachedDocument } from '@/types/chat';
import type { FileStatus } from '@/types/file';
import {
  collectApiCitations,
  findDraftCitationSourceId,
  getChatCitationOrder,
  getDraftVersionIds,
} from './utils/sourcesPanelCitations';
import type { CitationLinkTarget } from './components/DocumentEditor/draftCitationLinks';
import { citationIndexMapFromOrder, sortDraftCitationClusters } from './utils/draftCitationOrder';
import { getDraftLastSavedAt, setDraftLastSavedAt as persistDraftLastSavedAt } from './utils/draftSavedAtStorage';
import { draftDownloadFilename } from './utils/draftDownloadFilename';
import { getCitedCaseFiles } from './utils/getCitedCaseFiles';
import {
  downloadWithForcedFilename,
  getDownloadUrl,
  isAiGeneratedCitation,
  triggerProgrammaticDownload,
} from '@/utils';
import { classNames } from 'primereact/utils';
import type { GetDraftVersionResponse } from '@/api/chat';
import { BriefcaseIcon, globalToastService } from '@repo/atomic-components';
import { ChatWelcomeFader } from '@/ui/components/chat/ChatWelcome';
import { useMatterChatAddFiles } from '@/ui/features/chat/matterChat';
import { ProjectChatContext, useProjectChatContext } from '@/ui/features/chat/projectChat';
import { isDocumentViewerFileSizeTooLarge } from '@/ui/components/DocumentViewer/constants';

export interface ChatViewProps {
  /** Renders inside the case page file picker (no app header / outer template). */
  embedInCasePage?: boolean;
  /** Overrides chat input props when embedded on the case page (file selection from the list). */
  embedChatInputProps?: Partial<ChatInputProps>;
  /**
   * Case mention state for the new-chat page. The selected matter is passed as send context, so the
   * new chat is created with that case. Undefined for embedded chats.
   */
  caseMention?: CaseMentionState;
  /** The project the chat is open in: adds the way back to it and keeps the URL under it. */
  project?: { projectId: string; projectName?: string };
}

const CHAT_PANEL_ID = 'chat';
const SOURCES_PANEL_ID = 'sources';
const DRAFT_PANEL_ID = 'draft';
const CHAT_PANEL_MIN_SIZE = 348;
/** Width the chat panel keeps when hidden, enough for its "Show chat" button. */
const CHAT_RAIL_WIDTH = 48;

export const ChatView = ({ embedInCasePage = false, embedChatInputProps, caseMention, project }: ChatViewProps) => {
  const frostedGlassClass = useFrostedGlassClass();
  const chatDocumentPanelSettings = useAppSelector(selectChatDocumentPanelSettings);
  const historyRowPanel = useAppSelector(selectChatHistoryRowPanel);
  const chatDocumentsDownload = useAppSelector(selectChatDocumentsDownload);
  // The download modal lists everything the chat holds, drafts included — unlike
  // `chatDocuments`, which feeds the send payload and must stay draft-free.
  const allChatDocuments = useAppSelector(selectAllChatDocuments);
  const chatTitle = useAppSelector(selectChatTitle);
  const chatMatterInfo = useAppSelector(selectChatMatterInfo);
  const isChatCreator = useAppSelector(selectIsChatCreator);
  const activeDocumentSettings = useMemo(
    () => Object.values(chatDocumentPanelSettings).find(({ open }) => open),
    [chatDocumentPanelSettings],
  );
  const activeDocumentDownload = activeDocumentSettings
    ? chatDocumentsDownload[activeDocumentSettings?.documentId]
    : null;

  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as ChatPageLocationState | undefined;
  const { chatId: paramChatId } = useParams();
  // New-chat welcome view = no chat id in the URL and no case attached.
  const startWithExpandedInput = !paramChatId && !chatMatterInfo?.matterId;

  const matterIdForCaseBack = chatMatterInfo?.matterId;
  const { caseReference: caseRefForBack } = useGetMatterAboutQuery(
    { matterId: matterIdForCaseBack ?? '' },
    {
      skip: !matterIdForCaseBack,
      selectFromResult: ({ data }) => ({ caseReference: data?.info.caseReference }),
    },
  );

  // The History tab knows only `caseRef`, so look the matter id up from it.
  const caseRefToResolve = !matterIdForCaseBack ? chatMatterInfo?.matterReference : undefined;
  const { matterIdFromCaseRef } = useGetCasesQuery(
    { pageIndex: 0, pageSize: 1, search: caseRefToResolve },
    {
      skip: !caseRefToResolve,
      selectFromResult: ({ data }) => ({
        matterIdFromCaseRef: data?.matters.find((matter) => matter.caseReference === caseRefToResolve)?.matterId,
      }),
    },
  );

  const resolvedMatterId = matterIdForCaseBack ?? matterIdFromCaseRef;
  const caseRefToShow = caseRefForBack ?? chatMatterInfo?.matterReference;

  const [isInitialPromptApplied, setIsInitialPromptApplied] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  // Guards against re-sending the initial prompt on re-render (e.g. StrictMode).
  const hasAutoSentInitialPromptRef = useRef(false);

  const handleBackToCaseClick = () => {
    if (!resolvedMatterId) {
      return;
    }

    navigate(`/${AppRoutes.Cases}/${resolvedMatterId}`);
  };

  const {
    chatId,
    messages,
    chatCitations,
    isLoading,
    isWaitingForDocumentProcessing,
    messagesEndRef,
    chatDocuments,
    sessionDocuments,
    handleInputChange,
    handleSendMessage,
    handleFileChange,
    handleCitationClick,
    scrollContainerRef,
    resumeStickToBottom,
    isChatDocumentDownloading,
    handleChatDocumentClick,
    handleCitedDocumentClick,
    handleUploadFilesClick,
    canEditChat,
    clarifierQuestions,
    setChatQuery,
    handleToolkitPromptSelect,
  } = useChatContext();

  const hasClarifierQuestions = clarifierQuestions != null && clarifierQuestions.length > 0;

  const { matterChatInputProps } = useMatterChatAddFiles({
    caseId: resolvedMatterId,
    chatId,
    locationState,
    navigate,
    handleUploadFilesClick,
  });

  const caseFileAttachInputProps = embedInCasePage ? embedChatInputProps : matterChatInputProps;

  // Shares `useChatMessages`' cache entry; `currentData` so another chat's data never counts.
  const { isUnpromptedChatView } = useGetChatQuery(
    { chatId: paramChatId },
    {
      skip: !paramChatId,
      selectFromResult: ({ currentData }) => ({ isUnpromptedChatView: isUnpromptedChat(currentData) }),
    },
  );

  const { data: chatDocumentsApi } = useGetChatDocumentsQuery({ chatId }, { skip: !chatId });
  const documentsByMessageId = useMemo(() => {
    const map = new Map<string, MessageAttachedDocument[]>();
    for (const doc of chatDocumentsApi?.documents ?? []) {
      if (!doc.messageId) continue;
      const list = map.get(doc.messageId) ?? [];
      list.push(doc);
      map.set(doc.messageId, list);
    }
    return map;
  }, [chatDocumentsApi?.documents]);

  const promptAttachmentStatusById = useMemo(() => {
    const map = new Map<string, FileStatus>();
    for (const doc of sessionDocuments) {
      if (doc.isPromptAttachment === false) continue;
      map.set(doc.documentId, doc.status);
    }
    return map;
  }, [sessionDocuments]);

  const canEditChatEffective = chatId ? canEditChat !== false : true;

  const messageRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const chatPanelRef = useRef<PanelImperativeHandle>(null);
  const downloadBtnRef = useRef<HTMLButtonElement>(null);
  const sourcesBtnRef = useRef<HTMLButtonElement>(null);
  const backToCaseBtnRef = useRef<HTMLButtonElement>(null);
  const chatGroupRef = useRef<GroupImperativeHandle>(null);

  const {
    minimized: isChatInputMinimized,
    hidden: isChatInputHidden,
    expandChatInput,
    collapseChatInput,
    hideChatInput,
    showChatInput,
  } = useMinimizedChatInput({
    scopeKey: location.pathname,
    // The welcome hub (no chatId, no case selected) opens with the input expanded
    // so the user can start typing straight away; existing chats stay minimised.
    startExpanded: startWithExpandedInput,
    // The box lists these only while no reply is on its way.
    unsentFileCount: isLoading ? 0 : sessionDocuments.filter((doc) => doc.isPromptAttachment !== false).length,
  });

  const handleEditPrompt = useCallback(
    (prompt: string) => {
      setChatQuery(prompt);
      expandChatInput();
      requestAnimationFrame(() => {
        document.getElementById(CHAT_INPUT_TEXTAREA_ID)?.focus();
      });
    },
    [setChatQuery, expandChatInput],
  );

  const handleShowChatInput = useCallback(() => {
    showChatInput();
    // The dock remounts expanded on the next paint; hand the caret straight to the textarea.
    requestAnimationFrame(() => {
      document.getElementById(CHAT_INPUT_TEXTAREA_ID)?.focus();
    });
  }, [showChatInput]);

  /* Below `lg` there is no room for a side panel, so the panels stack instead. */
  const isCompactLayout = useIsCompactLayout();
  // `xl`: below it chat, Sources and a draft don't all fit their minimum widths.
  const hasRoomForSourcesBesideDraft = useMediaQuery('(min-width: 1280px)');

  const [triggerGetChatDocuments] = useLazyGetChatDocumentsQuery();
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const settingsBtnRef = useRef<HTMLButtonElement>(null);

  const { isDragging, handleDragEnter, handleDragLeave, handleDragOver, handleDrop } = useFileDragAndDrop({
    onFileChange: handleFileChange,
    isDisabled: matterChatInputProps.isAddMatterFilesModalVisible,
  });

  const draftVersionIds = useMemo(() => getDraftVersionIds(messages), [messages]);
  // A draft's citations count on from the message it sits under, so read the order each draft cites them in.
  const { data: draftCitationIds } = useGetDraftCitationIdsQuery(
    { draftVersionIds },
    { skip: draftVersionIds.length === 0 },
  );

  // One numbering for the whole chat — transcript, Sources panel and open draft alike.
  const globalOrderedUuids = useMemo(
    () => getChatCitationOrder(messages, chatCitations, draftCitationIds),
    [messages, chatCitations, draftCitationIds],
  );

  /** The Sources panel lists every citation in the chat, including draft-only ones, with stable numbers. */
  const sourcesPanelCitations = useMemo(
    () => globalOrderedUuids.map((id) => ({ id, ...chatCitations[id] })),
    [globalOrderedUuids, chatCitations],
  );

  // Whole-case chats cite documents outside `chatDocuments`, so fetch their names separately.
  const sourcesPanelDocuments = useSourcesPanelDocuments({ chatId, citations: sourcesPanelCitations, chatDocuments });

  const citedCaseFiles = useMemo(
    () => getCitedCaseFiles(sourcesPanelDocuments, sourcesPanelCitations, allChatDocuments ?? []),
    [sourcesPanelDocuments, sourcesPanelCitations, allChatDocuments],
  );

  // documentId -> documentName lookup used by citation tooltips.
  const documentNameById = useMemo(() => {
    const map = new Map<string, string>();
    sourcesPanelDocuments.forEach((doc) => {
      if (doc.documentId && doc.documentName) map.set(doc.documentId, doc.documentName);
    });
    return map;
  }, [sourcesPanelDocuments]);

  const dispatch = useAppDispatch();

  const [draftData, setDraftData] = useState<GetDraftVersionResponse | null>(null);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [draftSaveStatus, setDraftSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [draftLastSavedAt, setDraftLastSavedAt] = useState<Date | null>(null);
  // The Sources citation last picked from a draft in a project chat.
  const [draftSourceHighlight, setDraftSourceHighlight] = useState<{
    draftVersionId: string;
    id: string;
    trigger: number;
  } | null>(null);
  const [getDraftVersion] = useGetDraftVersionMutation();
  const [getDraftDownload, { isLoading: isDraftDownloading }] = useGetDraftDownloadMutation();
  const [saveDraft] = useSaveDraftMutation();
  const [refetchChatLazy] = useLazyGetChatQuery();
  const [fetchDraftCitationIds] = useLazyGetDraftCitationIdsQuery();
  // Last clicked draft — lets us ignore responses that arrive too late.
  const latestDraftRequestIdRef = useRef<string | null>(null);
  const activeDraftCitationIndexMapRef = useRef<Record<string, number>>({});
  const draftSaveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSavePayloadRef = useRef<{
    draftVersionId: string;
    content: string;
    citationIndexMap: Record<string, number>;
  } | null>(null);
  // Lets close/download wait for a save that is already running.
  const inflightSavePromiseRef = useRef<Promise<boolean> | null>(null);
  const projectChat = useProjectChatContext();
  const markProjectDraftEdited = projectChat?.markDraftEdited;

  const collectApiCitationOrder = useCallback(
    (chat: GetChatResponse, apiDraftCitationIds: Record<string, string[]>): string[] => {
      const apiMessages = chat.chatMessages ?? [];
      return getChatCitationOrder(
        apiMessages.map((msg) => ({ content: msg.message, drafts: msg.drafts })),
        collectApiCitations(apiMessages, chat.citations),
        apiDraftCitationIds,
      );
    },
    [],
  );

  const citationIndexMapCacheKey = useMemo(() => {
    const citationKeys = Object.keys(chatCitations).sort().join('|');

    return `${chatId ?? ''}:${messages.length}:${draftVersionIds.join('|')}:${citationKeys}`;
  }, [chatId, messages.length, draftVersionIds, chatCitations]);

  const citationIdsCacheRef = useRef<{
    chatId: string;
    cacheKey: string;
    citationIds: string[];
  } | null>(null);

  /** Citation order from the saved chat, so a draft opened right after a send uses the server's citations. */
  const getApiCitationOrder = useCallback(async (): Promise<string[]> => {
    if (!chatId) return [];

    const cached = citationIdsCacheRef.current;
    if (cached?.chatId === chatId && cached.cacheKey === citationIndexMapCacheKey) {
      return cached.citationIds;
    }

    const result = await refetchChatLazy({ chatId }, false).unwrap();
    const apiDraftVersionIds = getDraftVersionIds(result.chatMessages ?? []);
    // Same fallback as the Sources panel: a draft that can't be read numbers its citations last.
    const apiDraftCitationIds =
      apiDraftVersionIds.length > 0
        ? await fetchDraftCitationIds({ draftVersionIds: apiDraftVersionIds }, true)
            .unwrap()
            .catch(() => ({}))
        : {};
    const citationIds = collectApiCitationOrder(result, apiDraftCitationIds);

    citationIdsCacheRef.current = {
      chatId,
      cacheKey: citationIndexMapCacheKey,
      citationIds,
    };

    return citationIds;
  }, [chatId, citationIndexMapCacheKey, refetchChatLazy, fetchDraftCitationIds, collectApiCitationOrder]);

  const handleDraftClick = useCallback(
    async (draft: DraftInfo) => {
      // Skip if already showing this exact draft.
      if (activeDraftId === draft.draftVersionId && draftData) return;

      latestDraftRequestIdRef.current = draft.draftVersionId;

      try {
        const citationIds = await getApiCitationOrder();
        if (latestDraftRequestIdRef.current !== draft.draftVersionId) return;
        // The BE puts these numbers into the markdown, so send the chat's order to keep numbers
        // consistent. The same map is used on save and download to map numbers back to citation
        // ids.
        const citationIndexMap = citationIndexMapFromOrder(citationIds);
        activeDraftCitationIndexMapRef.current = citationIndexMap;
        const response = await getDraftVersion({
          draftVersionId: draft.draftVersionId,
          citationIndexMap,
        }).unwrap();
        // Ignore stale responses if a newer draft was requested in the meantime.
        if (latestDraftRequestIdRef.current !== draft.draftVersionId) return;

        // Groups of citations come back in the order the model emitted them, so
        // sort each group — otherwise a repeated citation trails its group.
        const content = sortDraftCitationClusters(response.content);

        // Draft content is ready — close any open document panel.
        if (activeDocumentSettings?.documentId) {
          dispatch(setChatDocumentPanelSettings({ ...activeDocumentSettings, open: false }));
        }
        dispatch(closeChatHistoryRowPanel());
        setActiveDraftId(draft.draftVersionId);
        setDraftData({ ...response, content });
        setDraftSaveStatus('idle');
        // The API carries no saved-at time, so restore the one we recorded.
        setDraftLastSavedAt(getDraftLastSavedAt(draft.draftVersionId));
      } catch (err) {
        if (latestDraftRequestIdRef.current !== draft.draftVersionId) return;
        console.error('Failed to fetch draft version:', err);
        globalToastService.show({
          closable: true,
          severity: 'error',
          summary: 'Could not load draft',
          detail: 'Please try again.',
          life: 6000,
        });
      }
    },
    [activeDraftId, draftData, activeDocumentSettings, dispatch, getDraftVersion, getApiCitationOrder],
  );

  // What the editor counts in the open draft, so unsaved edits count too.
  const [draftSearchMatchCount, setDraftSearchMatchCount] = useState<OpenDraftMatchCount | null>(null);

  const handleActiveDraftSearchMatch = useCallback(
    (draftVersionId: string) => {
      // Already open or loading.
      if (latestDraftRequestIdRef.current === draftVersionId) return;
      const draft = messages
        .flatMap((message) => message.drafts ?? [])
        .find((d) => d.draftVersionId === draftVersionId);
      if (draft) void handleDraftClick(draft);
    },
    [messages, handleDraftClick],
  );

  const chatSearch = useChatSearch({
    messages,
    messageRefs,
    scrollContainerRef,
    openDraftMatchCount: draftSearchMatchCount?.draftVersionId === activeDraftId ? draftSearchMatchCount : null,
    onActiveDraftMatch: handleActiveDraftSearchMatch,
  });

  const chatSearchQuery = chatSearch.searchQuery;
  const handleDraftSearchMatchCountChange = useCallback(
    (count: number) => {
      if (!activeDraftId) return;
      setDraftSearchMatchCount((prev) =>
        prev?.draftVersionId === activeDraftId && prev.query === chatSearchQuery && prev.count === count
          ? prev
          : { draftVersionId: activeDraftId, query: chatSearchQuery, count },
      );
    },
    [activeDraftId, chatSearchQuery],
  );
  const draftActiveSearchMatchIndex =
    chatSearch.activeDraftMatch?.draftVersionId === activeDraftId ? chatSearch.activeDraftMatch.index : -1;

  // Flush used when the editor is going away (chat switch / unmount): nothing
  // is left to show a status on, but the save time still needs recording.
  const flushSaveDraft = useCallback(
    (payload: { draftVersionId: string; content: string; citationIndexMap: Record<string, number> }) => {
      saveDraft(payload)
        .unwrap()
        .then(() => persistDraftLastSavedAt(payload.draftVersionId, new Date()))
        .catch(() => {
          // The editor is already gone, so there is nothing to retry into.
        });
    },
    [saveDraft],
  );

  // Saves the draft; on failure re-queues the payload so no edits are lost.
  const performSaveDraft = useCallback(
    async (payload: {
      draftVersionId: string;
      content: string;
      citationIndexMap: Record<string, number>;
    }): Promise<boolean> => {
      const { draftVersionId } = payload;
      setDraftSaveStatus('saving');
      try {
        await saveDraft(payload).unwrap();
        const savedAt = new Date();
        // Record every successful save, including ones whose status update is
        // skipped below, so reopening the draft shows the real last-saved time.
        persistDraftLastSavedAt(draftVersionId, savedAt);
        if (latestDraftRequestIdRef.current !== draftVersionId) return true;
        // Skip the "saved" status if newer edits are already queued.
        if (pendingSavePayloadRef.current === null) {
          setDraftSaveStatus('saved');
          setDraftLastSavedAt(savedAt);
        }
        return true;
      } catch (err) {
        console.error('Failed to save draft:', err);
        if (latestDraftRequestIdRef.current !== draftVersionId) return false;
        setDraftSaveStatus('error');
        // Re-queue this content so the next save retries it.
        if (pendingSavePayloadRef.current === null) {
          pendingSavePayloadRef.current = payload;
        }
        globalToastService.show({
          closable: true,
          severity: 'error',
          summary: 'Could not save draft',
          detail: 'Your latest draft changes were not saved. Please try again.',
          life: 6000,
        });
        return false;
      }
    },
    [saveDraft],
  );

  // Saves any unsaved edits now — used by close and download. An unedited draft is not saved, so
  // closing it can't overwrite someone else's newer version.
  const forceSaveCurrentDraft = useCallback(async (): Promise<boolean> => {
    if (!activeDraftId) return true;

    if (draftSaveDebounceRef.current) {
      clearTimeout(draftSaveDebounceRef.current);
      draftSaveDebounceRef.current = null;
    }

    // Wait for any save already in flight so we don't race.
    if (inflightSavePromiseRef.current) {
      await inflightSavePromiseRef.current;
    }

    // A failed save re-queues its payload, so this also retries it.
    const payload = pendingSavePayloadRef.current;
    if (!payload) return true;
    pendingSavePayloadRef.current = null;

    const savePromise = performSaveDraft(payload);
    inflightSavePromiseRef.current = savePromise;
    try {
      return await savePromise;
    } finally {
      if (inflightSavePromiseRef.current === savePromise) {
        inflightSavePromiseRef.current = null;
      }
    }
  }, [activeDraftId, performSaveDraft]);

  // Closes the draft after saving. Returns `false` if the save failed, so callers must await it.
  const handleCloseDraft = useCallback(async (): Promise<boolean> => {
    // Keep the draft open on failure, so the user can retry.
    const ok = await forceSaveCurrentDraft();
    if (!ok) return false;
    latestDraftRequestIdRef.current = null;
    activeDraftCitationIndexMapRef.current = {};
    setActiveDraftId(null);
    setDraftData(null);
    setDraftSaveStatus('idle');
    setDraftLastSavedAt(null);
    setDraftSourceHighlight(null);
    return true;
  }, [forceSaveCurrentDraft]);

  const handleDraftContentChange = useCallback(
    (content: string) => {
      if (!activeDraftId || !canEditChatEffective) return;

      markProjectDraftEdited?.(activeDraftId);

      const payload = {
        draftVersionId: activeDraftId,
        content,
        citationIndexMap: { ...activeDraftCitationIndexMapRef.current },
      };
      pendingSavePayloadRef.current = payload;

      // Don't call setDraftData here — it would remount the editor and lose the cursor.

      // Throttle, not debounce: later keystrokes only update the pending payload.
      if (!draftSaveDebounceRef.current) {
        draftSaveDebounceRef.current = setTimeout(() => {
          draftSaveDebounceRef.current = null;
          const latestPayload = pendingSavePayloadRef.current;
          // A flush from close/download may have already taken the payload.
          if (!latestPayload) return;
          pendingSavePayloadRef.current = null;

          const savePromise = performSaveDraft(latestPayload);
          inflightSavePromiseRef.current = savePromise;
          savePromise.finally(() => {
            if (inflightSavePromiseRef.current === savePromise) {
              inflightSavePromiseRef.current = null;
            }
          });
        }, 8000);
      }
    },
    [activeDraftId, canEditChatEffective, markProjectDraftEdited, performSaveDraft],
  );

  const handleDraftDownload = useCallback(async () => {
    if (!activeDraftId || !draftData) return;
    // Save first, so the download gets the latest content.
    const ok = await forceSaveCurrentDraft();
    if (!ok) return;
    try {
      const citationIndexMap = activeDraftCitationIndexMapRef.current;
      const { downloadPath, downloadToken } = await getDraftDownload({
        draftVersionId: activeDraftId,
        citationIndexMap,
      }).unwrap();
      const url = getDownloadUrl(downloadPath, downloadToken);
      if (!url) return;
      const filename = draftDownloadFilename(draftData.title);
      try {
        await downloadWithForcedFilename(url, filename);
      } catch {
        triggerProgrammaticDownload(url, filename);
      }
    } catch (err) {
      console.error('Failed to download draft:', err);
      globalToastService.show({
        closable: true,
        severity: 'error',
        summary: 'Download failed',
        detail: 'Could not download the draft. Please try again.',
        life: 6000,
      });
    }
  }, [activeDraftId, draftData, getDraftDownload, forceSaveCurrentDraft]);

  // Saves the open draft's pending edits first, so the project gets what the editor shows.
  const flushDraftForProjectSave = useEventCallback((draftVersionId: string) =>
    draftVersionId === activeDraftId ? forceSaveCurrentDraft() : Promise.resolve(true),
  );
  const projectChatWithFlush = useMemo(
    () =>
      projectChat && {
        ...projectChat,
        saveDraftToProject: (draft: DraftInfo) => projectChat.saveDraftToProject(draft, flushDraftForProjectSave),
      },
    [projectChat, flushDraftForProjectSave],
  );
  const draftProjectSave = useMemo(() => {
    if (!projectChatWithFlush || !activeDraftId || !draftData) return undefined;
    const draft = { draftVersionId: activeDraftId, title: draftData.title };

    return {
      state: projectChatWithFlush.getDraftSaveState(activeDraftId),
      hasChanges: projectChatWithFlush.hasDraftChangedSinceSave(activeDraftId),
      onSave: () => projectChatWithFlush.saveDraftToProject(draft),
    };
  }, [projectChatWithFlush, activeDraftId, draftData]);

  const [prevChatIdForDraft, setPrevChatIdForDraft] = useState(chatId);
  if (prevChatIdForDraft !== chatId) {
    setPrevChatIdForDraft(chatId);
    // Fire-and-forget: flush any pending save before switching chats
    if (pendingSavePayloadRef.current) {
      const payload = pendingSavePayloadRef.current;
      pendingSavePayloadRef.current = null;
      if (draftSaveDebounceRef.current) {
        clearTimeout(draftSaveDebounceRef.current);
        draftSaveDebounceRef.current = null;
      }
      flushSaveDraft(payload);
    } else if (draftSaveDebounceRef.current) {
      clearTimeout(draftSaveDebounceRef.current);
      draftSaveDebounceRef.current = null;
    }
    setActiveDraftId(null);
    setDraftData(null);
    setDraftSaveStatus('idle');
    setDraftLastSavedAt(null);
    activeDraftCitationIndexMapRef.current = {};
    citationIdsCacheRef.current = null;
    // Reset during render, not in an effect — an effect runs too late and the
    // previous chat's draft could still commit stale data.
    latestDraftRequestIdRef.current = null;
  }

  // Close the draft on citation click so the PDF replaces it. Skip when no draft is open, to avoid
  // a full transcript re-render.
  const handleCitationClickAndCloseDraft = useCallback(
    async (citation: Citation) => {
      if (activeDraftId || draftData) {
        const closed = await handleCloseDraft();
        if (!closed) return;
      }
      void handleCitationClick(citation);
    },
    [activeDraftId, draftData, handleCloseDraft, handleCitationClick],
  );

  // A file opened from its chip or the files list replaces the draft, as opening a draft replaces
  // the file. Only citations from Sources or the draft open beside it.
  const handleDocumentClickAndCloseDraft = useCallback(
    async (documentId: string) => {
      if (activeDraftId || draftData) {
        const closed = await handleCloseDraft();
        if (!closed) return;
      }
      await handleChatDocumentClick({ documentId, open: true, pageNumber: 1 });
    },
    [activeDraftId, draftData, handleCloseDraft, handleChatDocumentClick],
  );

  // Sources citations open next to the draft: Sources | Draft | PDF.
  const handleSourcesPanelCitationClick = useCallback(
    (settings: Parameters<typeof handleCitedDocumentClick>[0], documentName?: string) => {
      void handleCitedDocumentClick(settings, documentName);
    },
    [handleCitedDocumentClick],
  );

  // Citations inside the draft editor: same 3-column layout, conversation collapsed.
  const handleDraftCitationLinkClick = useCallback(
    ({ documentId, pageNumber, highlight }: { documentId: string; pageNumber: number; highlight: string }) => {
      if (!documentId) return;
      void handleCitedDocumentClick({
        open: true,
        documentId,
        pageNumber,
        highlightText: highlight || undefined,
        highlightTrigger: Date.now(),
      });
    },
    [handleCitedDocumentClick],
  );

  // Draft pills don't carry the AI-generated flag, so find it by number (index + 1 in the chat's order).
  const draftAiImageCitationNumbers = useMemo(() => {
    const numbers = new Set<number>();
    globalOrderedUuids.forEach((citationId, index) => {
      if (isAiGeneratedCitation(chatCitations[citationId])) numbers.add(index + 1);
    });
    return numbers;
  }, [globalOrderedUuids, chatCitations]);

  const areCitationsExist = sourcesPanelCitations.length > 0;
  const hasMessages = messages.length > 0;
  const chatDocument = chatDocuments.find((chatDoc) => chatDoc?.documentId === activeDocumentSettings?.documentId);
  // Whole-case chats can open a document not in `chatDocuments`, so fall back to the merged name map.
  const activeDocumentName =
    chatDocument?.documentName ??
    (activeDocumentSettings?.documentId ? documentNameById.get(activeDocumentSettings.documentId) : undefined);
  const isActiveDocumentTooLargeForPreview = isDocumentViewerFileSizeTooLarge(chatDocument?.sizeMb);
  const isDocumentPanelOpen = !!(
    activeDocumentSettings?.documentId &&
    (activeDocumentDownload || isActiveDocumentTooLargeForPreview)
  );
  // A history row details panel counts as the viewer panel for layout.
  const isViewerPanelOpen = isDocumentPanelOpen || !!historyRowPanel;
  // With a side panel open the toolkit renders inside the chat panel, not as a modal.
  const hasSidePanels = (!isLoading && areCitationsExist) || !!draftData || isViewerPanelOpen;
  // Draft + PDF leave no room for the conversation, so it collapses.
  const isDraftWithCitationOpen = !!draftData && isViewerPanelOpen;
  // With the draft and citations open, use the compact layout so Sources docks below the chat.
  const isCompactSourcesLayout = isCompactLayout || isDraftWithCitationOpen;
  // With a document, draft or history row beside it, the conversation can shrink to a rail.
  const canCollapseChat = !isCompactLayout && (!!draftData || isViewerPanelOpen);
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const focusComposerOnChatShowRef = useRef(false);
  const focusRailOnChatHideRef = useRef(false);
  const showChatBtnRef = useRef<HTMLButtonElement>(null);
  const lastExpandedChatSizeRef = useRef<number | null>(null);

  const handleChatPanelResize = useCallback((panelSize: PanelSize) => {
    const collapsed = panelSize.inPixels <= CHAT_RAIL_WIDTH + 1;
    if (!collapsed) lastExpandedChatSizeRef.current = panelSize.asPercentage;
    setIsChatCollapsed(collapsed);
  }, []);

  /**
   * `collapse()`/`expand()` trade width with the next panel, which is Sources even while it's
   * hidden, so move it to or from every open panel instead.
   */
  const setChatPanelSize = useCallback((toPercent: number) => {
    const group = chatGroupRef.current;
    if (!group) return;
    const layout = group.getLayout();
    const fromPercent = layout[CHAT_PANEL_ID];
    const otherIds = Object.keys(layout).filter((id) => id !== CHAT_PANEL_ID && layout[id] > 0);
    const othersTotal = otherIds.reduce((sum, id) => sum + layout[id], 0);
    if (fromPercent === undefined || othersTotal === 0) return;
    const delta = fromPercent - toPercent;
    const next: Record<string, number> = { ...layout, [CHAT_PANEL_ID]: toPercent };
    otherIds.forEach((id) => {
      next[id] = layout[id] + (delta * layout[id]) / othersTotal;
    });
    group.setLayout(next);
  }, []);

  const handleToggleChat = useCallback(() => {
    const size = chatPanelRef.current?.getSize();
    if (!size?.inPixels) return;
    const pxToPercent = size.asPercentage / size.inPixels;
    if (isChatCollapsed) {
      setChatPanelSize(Math.max(lastExpandedChatSizeRef.current ?? 0, CHAT_PANEL_MIN_SIZE * pxToPercent));
    } else {
      setChatPanelSize(CHAT_RAIL_WIDTH * pxToPercent);
    }
  }, [isChatCollapsed, setChatPanelSize]);

  const handleShowChatFromRail = useCallback(() => {
    focusComposerOnChatShowRef.current = true;
    handleToggleChat();
  }, [handleToggleChat]);

  const handleHideChatFromHeader = useCallback(() => {
    focusRailOnChatHideRef.current = true;
    handleToggleChat();
  }, [handleToggleChat]);

  // Opening a draft minimises the chat to its rail and gives the draft the freed width, unless the
  // chat is being searched. Two rAFs so the draft panel has mounted, otherwise the group overrides
  // the layout.
  const minimiseChatForDraft = useEventCallback(() => {
    if (chatSearch.isSearchOpen) return;
    const group = chatGroupRef.current;
    const size = chatPanelRef.current?.getSize();
    if (!canCollapseChat || !group || !size?.inPixels) return;
    const layout = group.getLayout();
    const fromPercent = layout[CHAT_PANEL_ID];
    const draftPercent = layout[DRAFT_PANEL_ID];
    if (fromPercent === undefined || draftPercent === undefined) return;
    // "Show chat" then splits the space evenly with the draft. The group may already have restored
    // the rail, so the chat's current width isn't a reliable size to go back to.
    lastExpandedChatSizeRef.current = (fromPercent + draftPercent) / 2;
    const chatPercent = (CHAT_RAIL_WIDTH * size.asPercentage) / size.inPixels;
    if (fromPercent <= chatPercent) return;
    group.setLayout({
      ...layout,
      [CHAT_PANEL_ID]: chatPercent,
      [DRAFT_PANEL_ID]: draftPercent + fromPercent - chatPercent,
    });
  });
  useEffect(() => {
    if (!activeDraftId) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(minimiseChatForDraft);
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [activeDraftId, minimiseChatForDraft]);

  // The button that was clicked unmounts with its side of the toggle, so move focus to the other side.
  useEffect(() => {
    if (isChatCollapsed && focusRailOnChatHideRef.current) {
      focusRailOnChatHideRef.current = false;
      showChatBtnRef.current?.focus();
    } else if (!isChatCollapsed && focusComposerOnChatShowRef.current) {
      focusComposerOnChatShowRef.current = false;
      document.getElementById(CHAT_INPUT_TEXTAREA_ID)?.focus();
    }
  }, [isChatCollapsed]);

  // Only the header toggle (or a draft citation) opens Sources. While closed it's disabled with no
  // minimum, so a drag next to it resizes the panels either side instead of pulling it open.
  const [isSourcesPanelOpen, setIsSourcesPanelOpen] = useState(false);
  // Set when Sources only fits beside a draft or document once that panel narrows.
  const [isSidePanelNarrowedForSources, setIsSidePanelNarrowedForSources] = useState(false);

  // Draft and document side by side, or one beside Sources on a tight screen, need a narrower
  // minimum to fit beside the chat on a laptop.
  const sidePanelMinSize =
    isDraftWithCitationOpen || (isSourcesPanelOpen && isSidePanelNarrowedForSources) ? 300 : undefined;

  // With both the draft and a citation open, shrink the chat panel to its minimum (or keep a hidden
  // chat on the rail) and split the rest between them. The whole layout is set, since the minimums
  // don't all fit on narrower screens and the group would otherwise collapse the chat and open
  // Sources. Two rAFs so the new panels have mounted, otherwise the group overrides the layout.
  useEffect(() => {
    if (!isDraftWithCitationOpen) return;
    // Read before the group re-lays out the new panels, which widens a hidden chat.
    const initialChatPx = chatPanelRef.current?.getSize().inPixels;
    const keepChatHidden = initialChatPx !== undefined && initialChatPx <= CHAT_RAIL_WIDTH + 1;
    let cancelled = false;
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        const group = chatGroupRef.current;
        const size = chatPanelRef.current?.getSize();
        if (cancelled || !group || !size?.inPixels) return;
        const layout = group.getLayout();
        const chatPx = keepChatHidden ? CHAT_RAIL_WIDTH : CHAT_PANEL_MIN_SIZE;
        const chatPercent = (chatPx * size.asPercentage) / size.inPixels;
        const sidePanelIds = Object.keys(layout).filter((id) => id !== CHAT_PANEL_ID && id !== SOURCES_PANEL_ID);
        const next: Record<string, number> = { ...layout, [CHAT_PANEL_ID]: chatPercent };
        if (SOURCES_PANEL_ID in layout) next[SOURCES_PANEL_ID] = 0;
        sidePanelIds.forEach((id) => {
          next[id] = (100 - chatPercent) / sidePanelIds.length;
        });
        group.setLayout(next);
      });
      if (cancelled) cancelAnimationFrame(raf2);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
    };
  }, [isDraftWithCitationOpen]);

  const sourcesPanelMinPercent = isCompactSourcesLayout ? 20 : 14;
  const lastSourcesPanelSizeRef = useRef<number | null>(null);
  const isOpeningSourcesPanelRef = useRef(false);

  /**
   * Resize Sources through the group rather than the panel handle: flipping `disabled` re-registers
   * the group, which restores the last layout set by a drag or `setLayout`, not by `expand()`.
   * Returns the layout the group applied.
   */
  const resizeSourcesPanel = useCallback((toPercent: number) => {
    const group = chatGroupRef.current;
    if (!group) return;
    const layout = group.getLayout();
    const ids = Object.keys(layout);
    const index = ids.indexOf(SOURCES_PANEL_ID);
    // Trade width with the next panel, or the chat when Sources is last, as `expand()` does.
    const neighbourId = ids[index + 1] ?? ids[index - 1];
    if (index < 0 || !neighbourId) return;
    const delta = toPercent - layout[SOURCES_PANEL_ID];
    return group.setLayout({
      ...layout,
      [SOURCES_PANEL_ID]: toPercent,
      [neighbourId]: layout[neighbourId] - delta,
    });
  }, []);

  const openSourcesPanel = useCallback(() => {
    const toPercent = Math.max(lastSourcesPanelSizeRef.current ?? 0, sourcesPanelMinPercent);
    // Enabling the panel re-registers the group, which reports Sources closed on the way.
    isOpeningSourcesPanelRef.current = true;
    // A disabled panel ignores every resize, so enable it before opening it.
    flushSync(() => {
      setIsSourcesPanelOpen(true);
      setIsSidePanelNarrowedForSources(false);
    });
    let layout = resizeSourcesPanel(toPercent);
    if (!layout?.[SOURCES_PANEL_ID]) {
      // The group keeps Sources shut when the panel minimums don't fit (200% zoom), so make room.
      flushSync(() => setIsSidePanelNarrowedForSources(true));
      layout = resizeSourcesPanel(toPercent);
    }
    isOpeningSourcesPanelRef.current = false;
    setIsSourcesPanelOpen(!!layout?.[SOURCES_PANEL_ID]);
  }, [resizeSourcesPanel, sourcesPanelMinPercent]);

  const closeSourcesPanel = useCallback(() => resizeSourcesPanel(0), [resizeSourcesPanel]);

  // Collapse the panel when entering the compact layout. It doesn't reopen automatically.
  const prevIsCompactLayoutRef = useRef(isCompactSourcesLayout);
  useEffect(() => {
    if (prevIsCompactLayoutRef.current === isCompactSourcesLayout) return;
    prevIsCompactLayoutRef.current = isCompactSourcesLayout;
    // The saved size is a share of the other axis now.
    lastSourcesPanelSizeRef.current = null;
    if (isCompactSourcesLayout) closeSourcesPanel();
  }, [isCompactSourcesLayout, closeSourcesPanel]);

  const handleToggleSourcesPanel = useCallback(() => {
    if (isSourcesPanelOpen) {
      closeSourcesPanel();
    } else {
      openSourcesPanel();
    }
  }, [closeSourcesPanel, isSourcesPanelOpen, openSourcesPanel]);

  const handleSourcesPanelResize = useCallback((panelSize: PanelSize) => {
    if (panelSize.inPixels <= 0) return;
    lastSourcesPanelSizeRef.current = panelSize.asPercentage;
    // Covers a panel that mounts open, in a project chat with a draft.
    setIsSourcesPanelOpen(true);
  }, []);

  // Disable Sources once it's closed, however that happened. This runs only once a drag ends, since
  // re-registering the panels mid-drag would drop the drag.
  const handleChatLayoutChanged = useCallback((layout: Layout) => {
    if (!layout[SOURCES_PANEL_ID] && !isOpeningSourcesPanelRef.current) setIsSourcesPanelOpen(false);
  }, []);

  const fileUrl = getDownloadUrl(activeDocumentDownload?.downloadPath, activeDocumentDownload?.downloadToken) ?? '';

  const handleClosePanel = (documentId: string) => {
    const settings = chatDocumentPanelSettings[documentId];
    if (settings) {
      dispatch(setChatDocumentPanelSettings({ ...settings, open: false }));
    }
  };

  useEffect(() => {
    if (!isInitialPromptApplied && locationState?.initialPrompt) {
      hasAutoSentInitialPromptRef.current = false;
      handleInputChange({
        target: { value: locationState.initialPrompt },
      } as ChangeEvent<HTMLTextAreaElement>);

      setIsInitialPromptApplied(true);
    }
  }, [handleInputChange, isInitialPromptApplied, locationState?.initialPrompt]);

  // After a case-mention send, clear the prompt — `useChat` treats this as a draft promotion and keeps it.
  useEffect(() => {
    if (paramChatId && caseMention?.selectedCase) {
      setChatQuery('');
    }
  }, [paramChatId, caseMention?.selectedCase, setChatQuery]);

  useAutoSendInitialChatPrompt({
    isInitialPromptApplied,
    locationState,
    pathname: location.pathname,
    hasAutoSentInitialPromptRef,
    handleSendMessage,
    triggerGetChatDocuments,
    navigate,
    chatId: chatId as string,
  });

  useEffect(() => {
    // Clear on unmount so these documents don't follow the user to the Cases page.
    return () => {
      if (pendingSavePayloadRef.current && draftSaveDebounceRef.current) {
        clearTimeout(draftSaveDebounceRef.current);
        flushSaveDraft(pendingSavePayloadRef.current);
      } else if (draftSaveDebounceRef.current) {
        clearTimeout(draftSaveDebounceRef.current);
      }
      dispatch(clearSessionDocuments());
    };
  }, [dispatch, flushSaveDraft]);

  const storeChatId = useAppSelector(selectChatId);
  const pendingUrlSyncChatId = useAppSelector(selectPendingUrlSyncChatId);
  const ignoreSharedLinkChatIdSync = useAppSelector(selectIgnoreSharedLinkChatIdSync);
  const isNewChatView = !paramChatId && !chatMatterInfo?.matterId;
  /** Show the welcome screen until the first prompt is sent — also on a chat a file upload created. */
  const showNewChatWelcomeHub = (isNewChatView || isUnpromptedChatView) && !hasMessages;
  // A project chat keeps its header on the welcome screen too, for the way back to the project.
  const isHeaderHidden = showNewChatWelcomeHub && !project;
  const chatPath = useCallback(
    (id: string) => (project ? projectChatPath(project.projectId, id) : `/${AppRoutes.Chat}/${id}`),
    [project],
  );

  // Hide the toolkit carousel once the input grows past a height threshold.
  const inputDockRef = useRef<HTMLDivElement>(null);
  const [isInputTall, setIsInputTall] = useState(false);
  useEffect(() => {
    const el = inputDockRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const height = entry.contentRect.height;
      const viewport = window.innerHeight;
      // Room the rest of the page needs; less under 700px tall, where the spacing is tighter.
      const reserved = viewport <= 700 ? 440 : 480;
      // Hide above `viewport - reserved`, show again 20px below that.
      setIsInputTall((prev) => (prev ? height > viewport - reserved - 20 : height > viewport - reserved));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [showNewChatWelcomeHub]);

  /** Same condition as the citations `Panel` below. */
  const isCitationsPanelRendered = !isLoading && areCitationsExist && !isNewChatView;
  /**
   * The group normalizes `defaultSize` across panels, so this has to complement the others for Sources to open at 14%.
   */
  const chatPanelDefaultSize = !hasSidePanels
    ? '100%'
    : isCitationsPanelRendered && !draftData && !isViewerPanelOpen
      ? '86%'
      : '43%';
  // The header toggle stays in place while the panel is open, so it can also close it.
  const showSourcesToggle = isCitationsPanelRendered;
  // In a project, a draft opens with Sources beside it: chat | Sources | draft.
  const opensSourcesWithDraft = !!project && !!draftData && !isViewerPanelOpen && hasRoomForSourcesBesideDraft;

  // In a project, a draft citation picks its source in Sources. With a document open beside the
  // draft Sources is hidden, so the citation opens in that document instead.
  const handleDraftCitationPillClick = useCallback(
    (target: CitationLinkTarget, number: number | null) => {
      const citationId =
        isCitationsPanelRendered && !isViewerPanelOpen
          ? findDraftCitationSourceId(globalOrderedUuids, chatCitations, target, number)
          : null;
      if (!citationId || !activeDraftId) {
        handleDraftCitationLinkClick(target);
        return;
      }
      if (!isSourcesPanelOpen) openSourcesPanel();
      setDraftSourceHighlight({ draftVersionId: activeDraftId, id: citationId, trigger: Date.now() });
    },
    [
      activeDraftId,
      chatCitations,
      globalOrderedUuids,
      handleDraftCitationLinkClick,
      isCitationsPanelRendered,
      isSourcesPanelOpen,
      isViewerPanelOpen,
      openSourcesPanel,
    ],
  );
  const sourcesPanelHighlight = draftSourceHighlight?.draftVersionId === activeDraftId ? draftSourceHighlight : null;
  const showBackToCase = Boolean(resolvedMatterId) && !embedInCasePage;
  // Icon-only, so the case reference has to live in the accessible name, not just the tooltip.
  const backToCaseLabel = `Back to case${caseRefToShow ? ` ${caseRefToShow}` : ''}`;
  const sourcesToggleLabel = isSourcesPanelOpen ? 'Hide sources' : 'Show sources';
  const downloadsButtonLabel = 'View and download files in this chat, or export full conversation history';

  // The header actions for the "…" menu that replaces the icons on a narrow chat panel.
  const headerMenuItems: ChatHeaderMenuItem[] = [];
  if (chatId && messages.length > 0 && !chatSearch.isSearchOpen) {
    headerMenuItems.push({
      label: 'Search chat',
      icon: <SearchIcon width={18} height={18} />,
      onSelect: chatSearch.openSearch,
    });
  }
  if (chatId && showSourcesToggle) {
    headerMenuItems.push({ label: sourcesToggleLabel, icon: <QuoteOutlineIcon />, onSelect: handleToggleSourcesPanel });
  }
  if (chatId && messages.length > 0) {
    headerMenuItems.push({
      label: 'Files in this chat',
      icon: <DocumentsIcon />,
      onSelect: () => setIsDownloadModalOpen(true),
    });
  }
  if (canEditChatEffective && isChatCreator) {
    headerMenuItems.push({
      label: 'Chat settings',
      icon: <SettingsOutlineIcon />,
      onSelect: () => setIsSettingsModalOpen(true),
    });
  }

  useEffect(() => {
    // Persist shared-link chatId in store.
    if (ignoreSharedLinkChatIdSync) return;

    if (paramChatId && paramChatId !== storeChatId) {
      dispatch(setChatId({ chatId: paramChatId }));
    }
  }, [dispatch, ignoreSharedLinkChatIdSync, paramChatId, storeChatId]);

  useEffect(() => {
    // A chat created in the background sets a flag — mirror its id into the URL.
    if (paramChatId) return;
    if (!pendingUrlSyncChatId) return;
    if (pendingUrlSyncChatId !== storeChatId) return;

    navigate(chatPath(pendingUrlSyncChatId), {
      replace: true,
      state: location.state,
    });
    dispatch(setPendingUrlSyncChatId(null));
  }, [chatPath, dispatch, location.state, navigate, paramChatId, pendingUrlSyncChatId, storeChatId]);

  useEffect(() => {
    if (ignoreSharedLinkChatIdSync && !paramChatId) {
      dispatch(setIgnoreSharedLinkChatIdSync(false));
    }
  }, [dispatch, ignoreSharedLinkChatIdSync, paramChatId]);

  useEffect(() => {
    // clear chat when ChatPage unmounts
    return () => {
      dispatch(clearChatButKeepId());
      dispatch(setIgnoreSharedLinkChatIdSync(false));
    };
  }, [dispatch]);

  const chatBody = (
    <div className="relative h-full w-full">
      {/* Outside the transcript: a live region must exist before the change it announces. */}
      <ChatStatusAnnouncer
        isLoading={isLoading}
        isWaitingForDocumentProcessing={isWaitingForDocumentProcessing}
        messages={messages}
      />
      {isChatDocumentDownloading && (
        <LoadingSpinner className="bg-fletchers-white/50 absolute inset-0 z-50 cursor-wait" />
      )}
      <Group
        orientation={isCompactLayout ? 'vertical' : 'horizontal'}
        groupRef={chatGroupRef}
        onLayoutChanged={handleChatLayoutChanged}
      >
        <Panel
          id={CHAT_PANEL_ID}
          defaultSize={chatPanelDefaultSize}
          minSize={CHAT_PANEL_MIN_SIZE}
          collapsible={canCollapseChat}
          collapsedSize={CHAT_RAIL_WIDTH}
          panelRef={chatPanelRef}
          onResize={handleChatPanelResize}
        >
          {canCollapseChat && isChatCollapsed && (
            <div className={classNames('flex h-full w-full flex-col items-center', frostedGlassClass)}>
              <div className="border-theme-border-grey flex h-14 w-full shrink-0 items-center justify-center border-b">
                <HeaderIconButton
                  ref={showChatBtnRef}
                  icon={<CommentIcon width={18} height={18} aria-hidden />}
                  tooltip="Show chat"
                  tooltipPosition="right"
                  onClick={handleShowChatFromRail}
                />
              </div>
            </div>
          )}
          <div
            className={classNames(
              /* No `justify-center`: it would push overflowing content out of reach. */
              'chat-panel-container relative flex h-full w-full flex-col items-center',
              // Kept mounted so the transcript and a half-typed prompt survive hiding the chat.
              { hidden: canCollapseChat && isChatCollapsed },
            )}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <DragAndDropOverlay
              isVisible={isDragging}
              fullScreen={!isViewerPanelOpen}
              className={classNames({
                'border-theme-dark-green m-[16px] rounded-[8px] border border-dashed': isViewerPanelOpen,
              })}
            />
            <div
              className={classNames(
                'border-theme-border-grey flex h-14 w-full shrink-0 items-center overflow-hidden border-b !px-4 @[30rem]:!px-12',
                frostedGlassClass,
                { hidden: isHeaderHidden },
              )}
            >
              <div className="mx-auto flex w-full max-w-240 items-center justify-between gap-2">
                {showBackToCase && (
                  <HeaderIconButton
                    ref={backToCaseBtnRef}
                    icon={<BackIcon pathClassName="fill-current" />}
                    tooltip={backToCaseLabel}
                    onClick={handleBackToCaseClick}
                  />
                )}
                <div
                  className={classNames(
                    'flex min-w-0 flex-1 shrink flex-col gap-0 leading-tight',
                    !project && '@[30rem]:flex-initial @[30rem]:flex-row @[30rem]:items-center @[30rem]:gap-1.5',
                  )}
                >
                  {project && (
                    <Link
                      to={`/${AppRoutes.Projects}/${project.projectId}`}
                      className={classNames(
                        'text-theme-dark-green flex w-fit max-w-full min-w-0 items-center gap-1 text-xs font-semibold hover:underline',
                        isEditingTitle && 'hidden',
                      )}
                    >
                      <BackIcon pathClassName="fill-current" width={16} height={16} aria-hidden className="shrink-0" />
                      <span className="truncate">Back to {project.projectName ?? 'project'}</span>
                    </Link>
                  )}
                  {chatId && chatTitle && (
                    <EditableChatTitle
                      chatId={chatId}
                      chatTitle={chatTitle}
                      canEditChat={Boolean(canEditChat)}
                      onEditingChange={setIsEditingTitle}
                    />
                  )}
                  {showBackToCase && caseRefToShow && (
                    <span
                      className={classNames(
                        'text-theme-text-grey shrink-0 text-left text-[0.8125rem] font-normal @[30rem]:text-[1.0625rem]',
                        isEditingTitle && 'hidden @[30rem]:block',
                      )}
                    >
                      {caseRefToShow}
                    </span>
                  )}
                </div>
                {chatId && (
                  <div
                    className={classNames(
                      'ml-auto flex items-center gap-0.5',
                      isEditingTitle && 'hidden @[30rem]:flex',
                    )}
                  >
                    {/* Below 480px the icons collapse into the "…" menu. */}
                    <div className="hidden items-center gap-0.5 @[30rem]:flex">
                      {chatId && messages.length > 0 && !chatSearch.isSearchOpen && (
                        <div className="shrink-0">
                          <ChatSearchBar search={chatSearch} />
                        </div>
                      )}

                      {chatId && showSourcesToggle && (
                        <HeaderIconButton
                          ref={sourcesBtnRef}
                          icon={<QuoteOutlineIcon />}
                          tooltip={sourcesToggleLabel}
                          onClick={handleToggleSourcesPanel}
                          active={isSourcesPanelOpen}
                          aria-expanded={isSourcesPanelOpen}
                        />
                      )}

                      {chatId && messages.length > 0 && (
                        <HeaderIconButton
                          ref={downloadBtnRef}
                          icon={<DocumentsIcon />}
                          tooltip={downloadsButtonLabel}
                          onClick={() => setIsDownloadModalOpen(true)}
                        />
                      )}

                      {canEditChatEffective && isChatCreator && (
                        <HeaderIconButton
                          ref={settingsBtnRef}
                          icon={<SettingsOutlineIcon />}
                          tooltip="Rename this chat and manage who can see it"
                          aria-label="Chat settings"
                          onClick={() => setIsSettingsModalOpen(true)}
                        />
                      )}
                    </div>

                    {headerMenuItems.length > 0 && (
                      <div className="@[30rem]:hidden">
                        <ChatHeaderMoreMenu items={headerMenuItems} />
                      </div>
                    )}

                    {canCollapseChat && (
                      <HeaderIconButton
                        icon={<MinimizeIcon width={18} height={18} className="rotate-90" aria-hidden />}
                        tooltip="Minimise the chat"
                        onClick={handleHideChatFromHeader}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
            <ChatSearchInputBar search={chatSearch} />

            <div className="relative flex min-h-0 w-full flex-1 flex-col" {...{ [CHAT_COLUMN_ATTR]: '' }}>
              <div className="flex min-h-0 w-full flex-1 flex-col">
                {showNewChatWelcomeHub ? (
                  // A size container, so the height breakpoints below are in rem and follow the text size.
                  <div className="relative flex min-h-0 w-full flex-1 flex-col [container:welcome-hub/size]">
                    <div className="relative flex h-full w-full min-w-0 flex-col overflow-hidden px-4 py-12 @[30rem]:px-12 [@container_welcome-hub_(max-height:43.75rem)]:py-6">
                      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col items-center justify-center gap-4">
                        <ChatWelcomeFader hidden={hasClarifierQuestions || isInputTall} />
                        <div
                          ref={inputDockRef}
                          className="w-full max-w-200 min-w-0"
                          {...{ [CHAT_INPUT_DOCK_ATTR]: '', [CHAT_INPUT_DOCK_HEIGHT_OFFSET_ATTR]: '120' }}
                        >
                          {/* Start expanded here, otherwise an unprompted `/chat/:id` would start
                             minimised. Case mention only on `/chat`. */}
                          <ChatInput
                            minimized={false}
                            isModal={!hasSidePanels}
                            {...caseFileAttachInputProps}
                            isSkipLinkTarget={!embedInCasePage}
                            frostedBackground
                            caseMention={isNewChatView ? caseMention : undefined}
                          />
                        </div>
                        <ChatDisclaimer className="w-full max-w-200 min-w-0" />
                      </div>
                      <div
                        inert={isInputTall}
                        className={classNames(
                          'mx-auto w-full max-w-300 min-w-0 overflow-hidden [@container_welcome-hub_(max-height:36.25rem)]:hidden',
                          'transition-[max-height,margin-top,opacity] duration-300 ease-out',
                          isInputTall
                            ? 'pointer-events-none mt-0 max-h-0 opacity-0'
                            : 'mt-12 max-h-[25rem] opacity-100 [@container_welcome-hub_(max-height:43.75rem)]:mt-6',
                        )}
                      >
                        <h2 className="text-theme-black mb-3 flex items-center justify-center gap-2 text-center text-[1rem] font-semibold">
                          <BriefcaseIcon width={20} height={20} aria-hidden />
                          Ways to get started
                        </h2>
                        <ToolkitCarousel onSelectPrompt={handleToolkitPromptSelect} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <ChatScrollableMessageArea
                    scrollContainerRef={scrollContainerRef}
                    onResumeScrollToBottom={resumeStickToBottom}
                    messagesEndRef={messagesEndRef}
                    messageRefs={messageRefs}
                    hasMessages={hasMessages}
                    searchQuery={chatSearch.searchQuery}
                    messages={messages}
                    onCitationClick={handleCitationClickAndCloseDraft}
                    globalOrderedUuids={globalOrderedUuids}
                    chatCitations={chatCitations}
                    isLoading={isLoading}
                    isWaitingForDocumentProcessing={isWaitingForDocumentProcessing}
                    documentsByMessageId={documentsByMessageId}
                    promptAttachmentStatusById={promptAttachmentStatusById}
                    onAttachedDocumentClick={handleDocumentClickAndCloseDraft}
                    canEditChat={canEditChatEffective}
                    onDraftClick={handleDraftClick}
                    onEditPrompt={handleEditPrompt}
                    onExpandChatInput={expandChatInput}
                    documentNameById={documentNameById}
                    clearsFloatingChatButton={canEditChatEffective && isChatInputHidden}
                  />
                )}
              </div>
              {/* Floats over the transcript so a minimised chat box gives the messages the full height. */}
              {canEditChatEffective && !showNewChatWelcomeHub && isChatInputHidden && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center px-4 py-2 lg:py-4 @[30rem]:px-12">
                  <div className="pointer-events-auto flex w-full max-w-240 justify-end">
                    <MinimizedChatInputButton onClick={handleShowChatInput} />
                  </div>
                </div>
              )}
              {canEditChatEffective && !showNewChatWelcomeHub && !isChatInputHidden && (
                <div
                  {...{ [CHAT_INPUT_DOCK_ATTR]: '' }}
                  className={classNames(
                    'flex w-full shrink-0 justify-center px-4 pt-2 pb-1.5 @[30rem]:px-12',
                    hasClarifierQuestions ? 'max-h-full overflow-y-auto' : 'max-h-[60%] overflow-hidden lg:max-h-none',
                  )}
                >
                  <div className="flex w-full max-w-240 flex-col items-center">
                    <div className="flex w-full justify-center">
                      <ChatInput
                        minimized={isChatInputMinimized}
                        onExpand={expandChatInput}
                        onCollapse={collapseChatInput}
                        onMinimize={hideChatInput}
                        isModal={!hasSidePanels}
                        caseRefForBack={caseRefToShow}
                        {...caseFileAttachInputProps}
                        isSkipLinkTarget={!embedInCasePage}
                        frostedBackground
                      />
                    </div>

                    <ChatDisclaimer className="mt-1.5 w-full" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </Panel>

        <Separator
          hidden={isLoading || !areCitationsExist || isDraftWithCitationOpen || isNewChatView}
          // A closed Sources has no edge to drag; the next divider over resizes the chat.
          disabled={!isSourcesPanelOpen}
          className="border-theme-border-grey border-b lg:border-r lg:border-b-0"
        />
        {isCitationsPanelRendered && (
          <Panel
            id={SOURCES_PANEL_ID}
            minSize={isSourcesPanelOpen ? `${sourcesPanelMinPercent}%` : 0}
            defaultSize={opensSourcesWithDraft ? '14%' : '0%'}
            // Opens at the design width but can be dragged wider.
            maxSize={isCompactSourcesLayout ? '50%' : '40%'}
            // Dragging it narrow still closes it; only the header toggle reopens it.
            collapsible
            collapsedSize={0}
            disabled={!isSourcesPanelOpen}
            onResize={handleSourcesPanelResize}
          >
            <CitationsPanel
              citations={sourcesPanelCitations}
              orderedUuids={globalOrderedUuids}
              onCitationOpen={handleSourcesPanelCitationClick}
              documents={sourcesPanelDocuments}
              onCollapse={closeSourcesPanel}
              highlightedCitation={sourcesPanelHighlight}
            />
          </Panel>
        )}

        {draftData && (
          <>
            <Separator
              hidden={isDraftWithCitationOpen && !areCitationsExist}
              className="border-theme-border-grey border-b lg:border-r lg:border-b-0"
            />
            <ChatDocumentSidePanel
              id={DRAFT_PANEL_ID}
              documentSettings={{
                open: true,
                documentId: '',
                pageNumber: 0,
                createdByAi: true,
              }}
              fileUrl=""
              documentName={draftData.title}
              draftContent={draftData.content}
              onClose={handleCloseDraft}
              onDownload={handleDraftDownload}
              isDownloading={isDraftDownloading}
              onCitationLinkClick={handleDraftCitationLinkClick}
              onCitationPillClick={project ? handleDraftCitationPillClick : undefined}
              documentNameById={documentNameById}
              aiImageDescriptionNumbers={draftAiImageCitationNumbers}
              onDraftContentChange={handleDraftContentChange}
              isDraftReadOnly={!canEditChatEffective}
              draftSaveStatus={draftSaveStatus}
              draftLastSavedAt={draftLastSavedAt}
              draftProjectSave={draftProjectSave}
              showDraftAlignment={!!project}
              minSize={sidePanelMinSize}
              draftSearchQuery={chatSearchQuery}
              draftActiveSearchMatchIndex={draftActiveSearchMatchIndex}
              onDraftSearchMatchCountChange={handleDraftSearchMatchCountChange}
            />
          </>
        )}

        {activeDocumentSettings?.documentId && (activeDocumentDownload || isActiveDocumentTooLargeForPreview) && (
          <>
            <Separator className="border-theme-border-grey border-b lg:border-r lg:border-b-0" />
            <ChatDocumentSidePanel
              documentSettings={activeDocumentSettings}
              fileUrl={fileUrl}
              documentName={activeDocumentName}
              documentSizeMb={chatDocument?.sizeMb}
              onClose={() => handleClosePanel(activeDocumentSettings.documentId)}
              minSize={sidePanelMinSize}
            />
          </>
        )}

        {historyRowPanel && (
          <>
            <Separator className="border-theme-border-grey border-b lg:border-r lg:border-b-0" />
            <ChatHistoryRowSidePanel
              historyRowId={historyRowPanel.historyRowId}
              matterId={historyRowPanel.matterId}
              onClose={() => dispatch(closeChatHistoryRowPanel())}
            />
          </>
        )}
      </Group>

      {chatId && (
        <ChatDownloadFilesModal
          visible={isDownloadModalOpen}
          onHide={() => setIsDownloadModalOpen(false)}
          chatId={chatId}
          files={allChatDocuments ?? []}
          citedCaseFiles={citedCaseFiles}
          onDraftClick={handleDraftClick}
          onDocumentClick={handleDocumentClickAndCloseDraft}
        />
      )}

      {chatId && isChatCreator && isSettingsModalOpen && (
        <ChatSettingsModal
          visible={isSettingsModalOpen}
          onHide={() => setIsSettingsModalOpen(false)}
          chatId={chatId}
          chatTitle={chatTitle ?? ''}
        />
      )}
    </div>
  );

  const chatContent = (
    <ProjectChatContext.Provider value={projectChatWithFlush}>{chatBody}</ProjectChatContext.Provider>
  );

  if (embedInCasePage) {
    return chatContent;
  }

  return <ChatTemplate>{chatContent}</ChatTemplate>;
};
